import React from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Avatar from '../ui/Avatar';
import { useT, useLocale } from '../../i18n/LocaleContext';
import { formatCurrency, formatDate } from '../../utils/format';
import { useData } from '../../data/DataContext';

const STATUS_TONE = { active: 'active', inactive: 'inactive', pending: 'pending' };

export default function MemberDetailModal({ isOpen, onClose, member, onEdit }) {
  const t = useT();
  const { locale } = useLocale();
  const { memberTotalPaid, payments } = useData();

  if (!member) return null;
  const totalPaid = memberTotalPaid(member.id);
  const recent = payments.filter((p) => p.memberId === member.id).slice(0, 5);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('members.view_member')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {onEdit && <Button variant="primary" onClick={onEdit}>{t('common.edit')}</Button>}
        </>
      )}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
        <Avatar firstName={member.firstName} lastName={member.lastName} size="lg" tone="gold" />
        <div>
          <div style={{ fontFamily: "'Cinzel', serif", fontSize: '16px', color: 'var(--blue-deep)' }}>
            {member.firstName} {member.lastName}
          </div>
          <div className="mono" style={{ marginTop: '4px' }}>{member.code || member.id}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Badge tone={STATUS_TONE[member.status]}>{t(`members.statuses.${member.status}`)}</Badge>
        </div>
      </div>

      <div className="detail-grid">
        <div>
          <div className="detail-item-label">{t('members.field_phone')}</div>
          <div className="detail-item-value">{member.phone || '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_email')}</div>
          <div className="detail-item-value">{member.email || '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_address')}</div>
          <div className="detail-item-value">{member.address || '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_parish')}</div>
          <div className="detail-item-value">{member.parish || '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_national_id')}</div>
          <div className="detail-item-value mono-value">{member.nationalId || '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_gender')}</div>
          <div className="detail-item-value">{member.gender ? t(`members.genders.${member.gender}`) : '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_marital_status')}</div>
          <div className="detail-item-value">{member.maritalStatus ? t(`members.marital_statuses.${member.maritalStatus}`) : '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_date_of_birth')}</div>
          <div className="detail-item-value">{member.dateOfBirth ? formatDate(member.dateOfBirth, t) : '—'}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_joined_at')}</div>
          <div className="detail-item-value">{formatDate(member.joinedAt, t)}</div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_total_paid')}</div>
          <div className="detail-item-value"><strong>{formatCurrency(totalPaid, locale)}</strong></div>
        </div>
        <div>
          <div className="detail-item-label">{t('members.field_notes')}</div>
          <div className="detail-item-value">{member.notes || '—'}</div>
        </div>
      </div>

      {recent.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div className="card-title" style={{ marginBottom: '10px' }}>{t('dashboard.recent_payments')}</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('common.reference')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('common.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{p.code || p.id}</td>
                    <td>{formatDate(p.date, t)}</td>
                    <td>{t(`payments.types.${p.type}`)}</td>
                    <td><strong>{formatCurrency(p.amount, locale)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
